/**
 * Single ranking list for Nanny B. Code items stay done.
 * Deploy + Tammy items stay open until a person finishes them.
 * Weekly items never close — they compound the map row.
 */

export type RankingOwner = 'code' | 'deploy' | 'tammy' | 'humberto' | 'weekly';
export type RankingStatus = 'done' | 'open';

export type RankingItem = {
  id: string;
  owner: RankingOwner;
  status: RankingStatus;
  title: string;
  how: string;
};

export const RANKING_NAP = [
  "Nanny B's Bakery",
  '62 Commerce Park Drive, Unit L',
  'Barrie, ON L4N 8W8',
  '(705) 503-4966',
  'https://nannybbakery.ca',
].join('\n');

export const GOOGLE_RANKING_CHECKLIST: RankingItem[] = [
  {
    id: 'faq-page',
    owner: 'code',
    status: 'done',
    title: 'Crawlable Q&A on /bakery-in-barrie',
    how: '13 visible questions + FAQ schema. Google reads the page, not a chat box.',
  },
  {
    id: 'crawler-faq-inject',
    owner: 'code',
    status: 'done',
    title: 'FAQ HTML and JSON-LD in the HTML shell',
    how: 'Server injects answers on /bakery-in-barrie so Googlebot does not need React.',
  },
  {
    id: 'bakery-schema-geo',
    owner: 'code',
    status: 'done',
    title: 'Bakery schema at Commerce Park',
    how: 'Geo 44.3251647, -79.6841505 (Google listing /g/11tj667fql). Not downtown Barrie.',
  },
  {
    id: 'sitemap-local',
    owner: 'code',
    status: 'done',
    title: 'Sitemap includes /bakery-in-barrie',
    how: 'Local sitemap and builder list the page. Live site gets it after deploy.',
  },
  {
    id: 'internal-links',
    owner: 'code',
    status: 'done',
    title: 'Home, Contact, and Footer link to the FAQ',
    how: 'Links go to /bakery-in-barrie#common-questions.',
  },
  {
    id: 'admin-copy',
    owner: 'code',
    status: 'done',
    title: 'Help and Sweetypie tell Tammy the real ranking work',
    how: 'No daily articles. Map row = Google Business. Website = FAQ page. Tammy vs Humberto lists are split.',
  },
  {
    id: 'crawler-hours',
    owner: 'code',
    status: 'done',
    title: 'Crawler Bakery schema includes shop hours',
    how: 'Tue–Fri 11:00–16:30, Sat 10:00–15:00 in JSON-LD Googlebot can read without React.',
  },
  {
    id: 'review-email-cta',
    owner: 'code',
    status: 'done',
    title: 'Ready-for-pickup email asks for a Google review',
    how: 'No discount. Link opens the Maps listing. Tammy still asks at the counter.',
  },
  {
    id: 'scaled-guides-noindex',
    owner: 'code',
    status: 'done',
    title: 'Stop indexing weekly AI guide clones',
    how: 'Date-stamped n8n slugs are noindex and omitted from the sitemap. Unique guides stay live.',
  },
  {
    id: 'product-schema-url',
    owner: 'code',
    status: 'done',
    title: 'Product schema uses the product page URL',
    how: 'JSON-LD @id is /product/:id. No fake aggregateRating.',
  },
  {
    id: 'pickup-returns-page',
    owner: 'code',
    status: 'done',
    title: 'Public pickup and returns page for Google Shopping',
    how: '/pickup-and-returns states pickup-only, no website shipping, contact for refunds. Footer + sitemap.',
  },
  {
    id: 'deploy-faq-page',
    owner: 'deploy',
    status: 'done',
    title: 'Deploy so Google can crawl the live FAQ page',
    how: 'Live sitemap includes https://nannybbakery.ca/bakery-in-barrie (verified 2026-08-13).',
  },
  {
    id: 'deploy-pickup-returns',
    owner: 'deploy',
    status: 'done',
    title: 'Deploy /pickup-and-returns so Google can crawl the policy URL',
    how: 'Live 2026-08-13: title Pickup and Returns; sitemap lists https://nannybbakery.ca/pickup-and-returns.',
  },
  {
    id: 'gsc-index-faq',
    owner: 'humberto',
    status: 'done',
    title: 'Request indexing of /bakery-in-barrie after deploy',
    how: 'Search Console → URL inspection → https://nannybbakery.ca/bakery-in-barrie → Request indexing. Do not resubmit the whole sitemap.',
  },
  {
    id: 'gsc-ai-opt-in',
    owner: 'humberto',
    status: 'open',
    title: 'Opt in to Search Console AI features if the toggle exists',
    how: 'Same GSC property. Only flip generative AI opt-in if Google shows it.',
  },
  {
    id: 'gsc-associate-ga',
    owner: 'humberto',
    status: 'done',
    title: 'Associate Search Console with Google Analytics',
    how: 'GSC Settings → Associations → Associate → Nanny B’s Bakery (G-YGFQF6VC1L) only. Do not pick Schedulo (G-B5H01FE1B5).',
  },
  {
    id: 'gsc-associate-merchant',
    owner: 'humberto',
    status: 'open',
    title: 'Associate Search Console with Merchant Center',
    how: 'GSC Settings → Associations → Associate → Merchant Center for Nanny B only. Do not pick Schedulo. Create merchants.google.com for the bakery if needed. Feed: https://nannybbakery.ca/api/feed/google.xml',
  },
  {
    id: 'gsc-shipping-policy',
    owner: 'humberto',
    status: 'done',
    title: 'Add Canada pickup shipping policy in Search Console',
    how: 'Settings → Shopping → Shipping and returns → Shipping → Add. Country Canada. Delivery 0–1 days. Cost Free. Matches $0 pickup in the feed. Do not add paid shipping.',
  },
  {
    id: 'gsc-returns-policy',
    owner: 'humberto',
    status: 'open',
    title: 'Add Canada returns policy pointing at /pickup-and-returns',
    how: 'After deploy. Settings → Shopping → Returns → Add. Country Canada. URL https://nannybbakery.ca/pickup-and-returns. Return window: No returns. Pending 10–13 days.',
  },
  {
    id: 'citation-barrie360',
    owner: 'humberto',
    status: 'open',
    title: 'Add the bakery on Barrie360',
    how: `https://barrie360.com/places/add-your-business/ — paste this NAP exactly:\n${RANKING_NAP}`,
  },
  {
    id: 'citation-tripadvisor',
    owner: 'humberto',
    status: 'open',
    title: 'Add or claim Tripadvisor',
    how: `https://www.tripadvisor.com/CreateListing.html — same NAP. Or claim at https://www.tripadvisor.com/Owners`,
  },
  {
    id: 'citation-yp-411',
    owner: 'humberto',
    status: 'open',
    title: 'Add Yellow Pages Canada (covers 411.ca)',
    how: `https://solutions.yp.ca/free-online-listing — same NAP. Then search 411.ca and claim if a listing appears.`,
  },
  {
    id: 'citation-yelp',
    owner: 'humberto',
    status: 'open',
    title: 'Add or claim Yelp Canada',
    how: `https://biz.yelp.com/signup_business — paste this NAP exactly:\n${RANKING_NAP}`,
  },
  {
    id: 'citation-barrietoday',
    owner: 'humberto',
    status: 'open',
    title: 'Get on BarrieToday bakeries directory',
    how: 'Requested free listing 2026-08-13 via advertising@barrietoday.com. Follow up if they ask; only accept free listing. Competitors Fox’s / Cakes By Design are already listed.',
  },
  {
    id: 'pause-n8n-weekly',
    owner: 'humberto',
    status: 'open',
    title: 'Pause the Monday n8n weekly guide generator',
    how: 'That workflow drafts similar “custom cakes Barrie” pages. Leave existing drafts unpublished. Google treats scaled similar pages as spam.',
  },
  {
    id: 'gbp-categories',
    owner: 'tammy',
    status: 'open',
    title: 'Add extra Google Business categories',
    how: 'business.google.com → primary Bakery. Add Cake shop, Cookie shop, Dessert shop.',
  },
  {
    id: 'gbp-nap-match',
    owner: 'tammy',
    status: 'open',
    title: 'Match Google listing to the website NAP',
    how: `Website must be https://nannybbakery.ca. Hours Tue–Fri 11–4:30, Sat 10–3, closed Sun–Mon. Address:\n${RANKING_NAP}`,
  },
  {
    id: 'gbp-maps-qa',
    owner: 'tammy',
    status: 'open',
    title: 'Answer Google Maps questions with the same FAQ words',
    how: 'Business Profile → Questions. Copy answers from /bakery-in-barrie#common-questions.',
  },
  {
    id: 'leave-ai-drafts',
    owner: 'tammy',
    status: 'open',
    title: 'Leave weekly AI article drafts unpublished',
    how: 'Articles admin. A few original guides are enough. The FAQ page already answers cake and hours questions.',
  },
  {
    id: 'reviews-ask',
    owner: 'weekly',
    status: 'open',
    title: 'Ask for a Google review at every happy pickup',
    how: 'Say: “Would you leave us a Google review?” Reply to every review.',
  },
  {
    id: 'gbp-weekly-post',
    owner: 'weekly',
    status: 'open',
    title: 'Post one Google photo or update each week',
    how: 'New cake, Saturday special, or storefront. This is the daily compounding — not new articles.',
  },
];

export function rankingItemsByOwner(owner: RankingOwner) {
  return GOOGLE_RANKING_CHECKLIST.filter((item) => item.owner === owner);
}

export function openRankingItems() {
  return GOOGLE_RANKING_CHECKLIST.filter((item) => item.status === 'open');
}

/** Tammy's portal: only shop + Google Business work. */
export function tammyOpenRankingItems() {
  return openRankingItems().filter((item) => item.owner === 'tammy' || item.owner === 'weekly');
}

/** Developer work after code is done (deploy, GSC, directories). */
export function humbertoOpenRankingItems() {
  return openRankingItems().filter((item) => item.owner === 'humberto' || item.owner === 'deploy');
}

/** Help “Show up on Google” steps: Tammy only. */
export function helpShowUpOnGoogleSteps() {
  return tammyOpenRankingItems().map((item) => `${item.title}: ${item.how.split('\n')[0]}`);
}
